import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface BrandMarkProps {
  /** Size in pixels (sets width & height). */
  size?: number;
  /** Animation mode. */
  variant?: "static" | "spin" | "float" | "reveal-in-view" | "loader";
  className?: string;
  /** aria-label; if omitted the mark is decorative. */
  ariaLabel?: string;
  /** Optional opacity (0-1). */
  opacity?: number;
}

/**
 * OSS beeldmerk — gebruikt het échte logo (public/favicon.png) met
 * verschillende animatievarianten eromheen.
 */
export function BrandMark({
  size = 48,
  variant = "static",
  className,
  ariaLabel,
  opacity,
}: BrandMarkProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(variant !== "reveal-in-view");

  useEffect(() => {
    if (variant !== "reveal-in-view" || !ref.current) return;
    const el = ref.current;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [variant]);

  const decorative = !ariaLabel;
  const imgAlt = decorative ? "" : ariaLabel;

  // Loader: rotating ring around a static logo
  if (variant === "loader") {
    return (
      <div
        ref={ref}
        className={cn("relative inline-flex items-center justify-center", className)}
        style={{ width: size, height: size, opacity }}
        role={decorative ? undefined : "img"}
        aria-label={ariaLabel}
        aria-hidden={decorative || undefined}
      >
        <svg
          viewBox="0 0 100 100"
          width={size}
          height={size}
          className="absolute inset-0 animate-brand-spin"
          aria-hidden="true"
        >
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth={3}
            strokeLinecap="round"
            strokeDasharray="70 220"
            opacity={0.55}
          />
        </svg>
        <img
          src="/favicon.png"
          alt={imgAlt}
          width={size * 0.78}
          height={size * 0.78}
          className="relative"
          loading="lazy"
          decoding="async"
        />
      </div>
    );
  }

  const animClass =
    variant === "spin"
      ? "animate-brand-spin"
      : variant === "float"
      ? "animate-brand-float"
      : variant === "reveal-in-view"
      ? inView
        ? "animate-brand-reveal"
        : "opacity-0 scale-90"
      : undefined;

  return (
    <div
      ref={ref}
      className={cn("inline-flex items-center justify-center", className)}
      style={{ width: size, height: size, opacity }}
      role={decorative ? undefined : "img"}
      aria-label={ariaLabel}
      aria-hidden={decorative || undefined}
    >
      <img
        src="/favicon.png"
        alt={imgAlt}
        width={size}
        height={size}
        className={cn("block", animClass)}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

export default BrandMark;
